import { GET_ORGANIZATION_SUBSCRIPTION_QUERY } from '@/graphql/organization.gql';
import {
  GET_DEFAULT_PRICING_QUERY,
  GET_ORGANIZATION_PLAN_STATUS_QUERY
} from '@/graphql/pricing.gql';
import LifetimeCountdownComponent from '@/components/lifetime-offer/lifetime-countdown.component';
import { useLifetimeOffer } from '@/hooks/use-lifetime-offer';
import { getProtectedAxiosInstance } from '@/services/axios/Axios';
import {
  createLifetimeCheckoutUrl,
  getLifetimeCheckoutErrorMessage,
  useResetLoadingOnBackNavigation
} from '@/services/stripe/lifetime-checkout.service';
import { useOrganizationStore } from '@/stores/organization.store';
import { Organization } from '@/types/organization.type';
import {
  PricingLimitation,
  PricingPlan,
  PricingPlanType,
  PricingSubscription,
  PricingVersion
} from '@/types/pricing.type';
import { useApolloClient, useQuery } from '@apollo/client';
import { useUserEmail, useUserId } from '@nhost/nextjs';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  CLCustomSelectOption,
  CLPrimaryCustomButton,
  CLSecondaryCustomButton
} from '../ui-controls';
import CLButton from '../ui-controls/default-ui-controls/button';
import AlertPopupComponent from '../utils/alert-popup.component';
import { LoadingSpinBlackCustom } from '../utils/loading.component';
import ChurnSurveyPopupComponent from './churn-survey-popup.component';
import PaymentStatusPopupComponent from './payment-status-popup.component';
import PostSubscriptionPopupComponent from './post-subscription-popup.component';

// Activation happens on a Stripe webhook, on a different connection to the
// redirect that brings the user back here - so the plan is usually, but not
// always, live by the time they land. Poll rather than trust the redirect.
const ACTIVATION_POLL_INTERVAL_MS = 2000;
const ACTIVATION_POLL_TIMEOUT_MS = 30000;

// Lifetime is presented on its own, above the subscription grid, so it gets
// accents of its own rather than a slot in `planColors`: amber for the accent
// bar and CTA, cream for the countdown chip and feature checks.
const LIFETIME_ACCENT_COLOR = '#E8A94F';
const LIFETIME_CREAM_COLOR = '#FAF0D3';

// Labels for the `pricing_limitations.type` values stored against a plan.
const LIMITATION_LABELS: { [key: string]: string } = {
  users: 'team members',
  projects: 'projects',
  checklists: 'checklists',
  runs: 'runs',
  ai_checklists: 'AI checklists'
};

export default function PlanAndBillingComponent() {
  // Variables
  const planColors = ['#9BE7FF', '#FFB2FF', '#B2FAB4', '#ff8a80'];
  const router = useRouter();
  const { subscription } = router.query;
  // Set by Stripe Checkout when it redirects the user back here.
  const checkoutStatus = router.query?.['checkout'] as string | undefined;
  const userEmail = useUserEmail();
  const userId = useUserId();

  // States
  const [organization, setOrganization] = useState<Organization>();
  const [largeQuantity, setLargeQuantity] = useState<number>(20);
  const [smallQuantity, setSmallQuantity] = useState<number>(5);
  const [pricingVersion, setPricingVersion] = useState<PricingVersion>();
  const [currentSubscriptionProductId, setCurrentSubscriptionProductId] =
    useState<string>();
  const [currentSubscriptionQty, setCurrentSubscriptionQty] =
    useState<number>();
  const [planTypes, setPlanTypes] = useState<PricingPlanType[]>();
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>();
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>();
  const [selectedProductId, setSelectedProductId] = useState<string>();
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [totalOrgMembers, setTotalOrgMembers] = useState<number>(1);
  const [isAlertPopupOpen, setIsAlertPopupOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isCustomerPortalLoading, setIsCustomerPortalLoading] =
    useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [isPostSubscriptionPopupOpen, setIsPostSubscriptionPopupOpen] =
    useState<boolean>(false);
  const [isChurnSurveyPopupOpen, setIsChurnSurveyPopupOpen] =
    useState<boolean>(false);
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false);
  const [
    isCancelOrReactivateSubscriptionLoading,
    setIsCancelOrReactivateSubscriptionLoading
  ] = useState<boolean>(false);
  const [isLifetimeCheckoutLoading, setIsLifetimeCheckoutLoading] =
    useState<boolean>(false);

  // Backing out of Stripe Checkout restores this page from the back/forward
  // cache with the claim spinner still on; clear it.
  useResetLoadingOnBackNavigation(setIsLifetimeCheckoutLoading);
  const [activationState, setActivationState] = useState<
    'idle' | 'pending' | 'activated' | 'timed-out'
  >('idle');

  // Store
  const { selectedOrganization, updateOrganization } = useOrganizationStore();

  // The 24 hour claim window, counted from when the organization was created.
  const lifetimeOffer = useLifetimeOffer();

  // GraphQL query
  const apolloClient = useApolloClient();
  const { loading, data } = useQuery<{ pricing_versions: PricingVersion[] }>(
    GET_DEFAULT_PRICING_QUERY,
    {
      variables: {}
    }
  );
  const {
    loading: orgLoading,
    data: orgData,
    refetch: refetchOrganization
  } = useQuery<{
    organization: Organization[];
  }>(GET_ORGANIZATION_SUBSCRIPTION_QUERY, {
    variables: {
      orgId: selectedOrganization?.id
    },
    fetchPolicy: 'network-only'
  });

  // A lifetime plan is a one-time purchase, so it does not belong in a grid
  // governed by a monthly/yearly toggle. It gets its own section above, and
  // the subscription grid keeps the four-column layout it was built for.
  const lifetimePlanType = planTypes?.find(planType => planType?.is_lifetime);
  const subscriptionPlanTypes = planTypes?.filter(
    planType => !planType?.is_lifetime
  );

  useEffect(() => {
    if (subscription) {
      const surveyKey = `${userId}-hasSeenProSurvey`; // Unique key per user
      const hasSeenSurvey = localStorage.getItem(surveyKey);

      if (!hasSeenSurvey) {
        setIsPostSubscriptionPopupOpen(true);
        localStorage.setItem(surveyKey, 'true');
      }
    }
  }, [subscription, userId]);

  useEffect(() => {
    if (data && data?.pricing_versions?.length > 0) {
      const selectedPricingVersion = data.pricing_versions[0];
      if (selectedPricingVersion?.pricing_currencies?.length > 0) {
        setSelectedCurrencyId(selectedPricingVersion?.pricing_currencies[0].id);
      }

      if (selectedPricingVersion?.pricing_periods?.length > 0) {
        const defaultPeriod = selectedPricingVersion?.pricing_periods?.find(
          period => period?.is_default_selection
        );
        if (defaultPeriod) {
          setSelectedPeriodId(defaultPeriod.id);
        }
      }

      if (selectedPricingVersion?.pricing_plan_types?.length > 0) {
        setPlanTypes(selectedPricingVersion?.pricing_plan_types);
      }
      // console.log('selectedPricingVersion:', selectedPricingVersion);

      setPricingVersion(selectedPricingVersion);
    }

    if (
      orgData &&
      orgData?.organization?.length > 0 &&
      orgData.organization[0]?.pricing_subscription
    ) {
      const organization = orgData.organization[0];
      setOrganization(organization);

      // Set current plan product_id
      if (
        organization?.pricing_subscription?.pricing_plan?.pricing_price
          ?.product_id
      ) {
        setCurrentSubscriptionProductId(
          organization?.pricing_subscription?.pricing_plan?.pricing_price
            ?.product_id
        );
      }

      const freshSubscription = organization.pricing_subscription;

      // The store's copy of the organization can predate its subscription: a
      // freshly created org lands in the store before its plan is linked, so
      // `selectedOrganization.pricing_subscription` is undefined there while
      // the query above already sees one. Fall back to what we just fetched
      // rather than spreading undefined.
      if (
        selectedOrganization &&
        freshSubscription?.pricing_plan?.pricing_plan_type
      ) {
        const storedSubscription = selectedOrganization.pricing_subscription;

        updateOrganization({
          ...selectedOrganization,

          // overwrite *only* the subscription subtree
          pricing_subscription: {
            ...(storedSubscription ?? freshSubscription),

            // Seat count and status gate invites and upgrade prompts app-wide,
            // so they have to move with the plan - otherwise a user who just
            // bought lifetime still sees the old plan's limits.
            status: freshSubscription.status,
            qty: freshSubscription.qty,

            // overwrite the plan subtree
            pricing_plan: {
              ...(storedSubscription?.pricing_plan ??
                freshSubscription.pricing_plan),

              // finally overwrite the plan_type
              pricing_plan_type:
                freshSubscription.pricing_plan.pricing_plan_type,
              pricing_limitations:
                freshSubscription.pricing_plan.pricing_limitations ?? []
            }
          }
        });
      }

      // Set current subscription quantity
      if (
        organization?.pricing_subscription &&
        organization?.pricing_subscription?.qty >= 1
      ) {
        const planName = organization?.pricing_subscription?.pricing_plan?.name;

        if (planName?.startsWith('Large')) {
          setCurrentSubscriptionQty(largeQuantity);
        } else if (planName?.startsWith('Small')) {
          setCurrentSubscriptionQty(smallQuantity);
        } else if (planName?.startsWith('Starter')) {
          setCurrentSubscriptionQty(3);
        }

        // If the current plan is not free plan, set the selected quantity
        if (
          !organization?.pricing_subscription?.pricing_plan?.pricing_plan_type
            ?.is_freeplan
        ) {
          const planName =
            organization?.pricing_subscription?.pricing_plan?.name;

          if (planName?.startsWith('Large')) {
            setSelectedQuantity(largeQuantity);
          } else if (planName?.startsWith('Small')) {
            setSelectedQuantity(smallQuantity);
          } else if (planName?.startsWith('Starter')) {
            setSelectedQuantity(3);
          }
        }
      }

      // Set min quantity
      if (
        organization?.org_users_aggregate &&
        organization?.org_invites_aggregate
      ) {
        const _totalOrgMembers =
          (organization?.org_users_aggregate?.aggregate?.count || 0) +
          (organization?.org_invites_aggregate?.aggregate?.count || 0);
        setTotalOrgMembers(_totalOrgMembers);

        // If the current plan is free plan, set the selected quantity
        if (
          organization?.pricing_subscription?.pricing_plan?.pricing_plan_type
            ?.is_freeplan
        ) {
          setSelectedQuantity(_totalOrgMembers);
        }
      }

      // Check the subscription status
      if (
        organization?.pricing_subscription &&
        checkSubscriptionActiveStatus(organization?.pricing_subscription)
      ) {
        setIsSubscriptionActive(true);
      }
    }
  }, [data, orgData]);

  // Poll for lifetime activation after the user returns from Stripe Checkout.
  // `?checkout=success` is only a hint - anyone can type it - so the plan read
  // back from the API stays the single source of truth.
  useEffect(() => {
    const orgId = selectedOrganization?.id;

    if (checkoutStatus !== 'success' || !orgId) {
      return;
    }

    let cancelled = false;
    setActivationState('pending');

    (async () => {
      const deadline = Date.now() + ACTIVATION_POLL_TIMEOUT_MS;

      while (!cancelled && Date.now() < deadline) {
        try {
          const { data: planStatus } = await apolloClient.query<{
            organization: { pricing_subscription?: PricingSubscription }[];
          }>({
            query: GET_ORGANIZATION_PLAN_STATUS_QUERY,
            variables: { orgId },
            fetchPolicy: 'network-only'
          });

          const activeSubscription =
            planStatus?.organization?.[0]?.pricing_subscription;

          if (
            activeSubscription?.status === 'active' &&
            activeSubscription?.pricing_plan?.pricing_plan_type?.is_lifetime
          ) {
            // Refetch the full subscription so the current-plan card, seat
            // count and usage limits all stop showing the previous plan.
            await refetchOrganization();
            if (!cancelled) {
              setActivationState('activated');
            }
            return;
          }
        } catch (error) {
          // A failed poll is not a failed payment - the webhook may simply not
          // have landed yet. Keep trying until the deadline.
          console.error(error);
        }

        await new Promise(resolve =>
          setTimeout(resolve, ACTIVATION_POLL_INTERVAL_MS)
        );
      }

      if (!cancelled) {
        setActivationState('timed-out');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    checkoutStatus,
    selectedOrganization?.id,
    apolloClient,
    refetchOrganization
  ]);

  // Show loading
  if (loading || orgLoading) {
    return <></>;
  }

  function getPricingPlan(planType: PricingPlanType): PricingPlan | undefined {
    if (!(planType?.pricing_plans?.length > 0)) {
      return;
    }

    // A lifetime plan sits on its own `lifetime` period, which is deliberately
    // kept out of the monthly/yearly toggle - so match on currency alone and
    // let the card show regardless of which period is selected.
    if (planType?.is_lifetime) {
      return planType.pricing_plans.find(
        plan => plan.pricing_currency_id === selectedCurrencyId
      );
    }

    return planType.pricing_plans.find(
      plan =>
        plan.pricing_currency_id === selectedCurrencyId &&
        plan.pricing_period_id === selectedPeriodId
    );
  }

  type PricingPlanPriceProps = {
    planType: PricingPlanType;
    totalOrgMembers: number;
    selectedQuantity: number;
    setSelectedQuantity: (val: number) => void;
    setInfoMessage: (msg: string) => void;
    setIsAlertPopupOpen: (val: boolean) => void;
  };

  const PricingPlanPrice: React.FC<PricingPlanPriceProps> = ({
    planType,
    totalOrgMembers,
    selectedQuantity,
    setSelectedQuantity,
    setInfoMessage,
    setIsAlertPopupOpen
  }) => {
    const pricingPlan = getPricingPlan(planType);

    useEffect(() => {
      if (!pricingPlan) return;

      // if (pricingPlan.name?.startsWith('Large')) {
      //   setSelectedQuantity(20);
      // }

      // if (pricingPlan.name?.startsWith('Small')) {
      //   setSelectedQuantity(5);
      // }
    }, [pricingPlan?.name]);

    if (planType?.is_enterprise) {
      const planTypeIndex = 3;
      const enterprisePlanType = {
        pricing_plan_type_features: [
          { name: 'Custom Members' },
          { name: 'Priority Support' },
          { name: 'SSO (SAML) for Login' }
        ]
      };
      return (
        <div className="flex gap-2 items-center">
          <div className="text-4xl font-semibold">Let&apos;s talk</div>
        </div>
      );
    }

    if (!pricingPlan) return <></>;

    if (planType?.is_freeplan) {
      return (
        <div className="flex gap-2 items-center">
          <div className="text-4xl font-semibold">
            ${pricingPlan.pricing_price.amount_per_month}
          </div>
          <div className="opacity-60">per Member/mo</div>
        </div>
      );
    }

    return (
      <div className="flex gap-2 items-center">
        <div className="text-4xl font-semibold">
          ${pricingPlan.pricing_price.amount_per_month}
        </div>
        <div className="flex flex-wrap items-center gap-[2px]">
          <span className="opacity-60">for</span>
          {pricingPlan.name?.startsWith('Small') ? (
            <span className="text-sm font-medium px-1 rounded-lg">
              {smallQuantity}
            </span>
          ) : pricingPlan.name?.startsWith('Large') ? (
            <span className="text-sm font-medium px-1 rounded-lg">
              {largeQuantity}
            </span>
          ) : (
            <CLCustomSelectOption
              className="text-sm pt-[4px] pr-[30px] pb-[4px] pl-[10px] rounded-lg"
              value={selectedQuantity}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                const selected = parseInt(event.target.value);
                if (selected < totalOrgMembers) {
                  setInfoMessage(
                    `Planning to downgrade your plan? Please make sure to first remove any extra team members to fit the new plan's team size limit.`
                  );
                  setIsAlertPopupOpen(true);
                } else {
                  setSelectedQuantity(selected);
                }
              }}
            >
              {Array.from({ length: 50 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </CLCustomSelectOption>
          )}
          <span className="opacity-60">Member(s)/mo</span>
        </div>
      </div>
    );
  };

  function getPricingTypeFeatures(
    accentColor: string,
    planType: PricingPlanType,
    // The lifetime section is wide, so it lays the same list out in columns.
    containerClassName = 'flex flex-col gap-2'
  ) {
    const pricingPlan = getPricingPlan(planType);

    // The lifetime plan type has no feature rows configured, so fall back to
    // what the plan actually grants. Both sides come from the pricing tables -
    // nothing here is hardcoded.
    const features: { name: string; is_title?: boolean }[] =
      planType?.pricing_plan_type_features?.length > 0
        ? planType.pricing_plan_type_features
        : (pricingPlan?.pricing_limitations ?? []).map(
            (limitation: PricingLimitation) => ({
              name: `${limitation.value} ${
                LIMITATION_LABELS[limitation.type] ?? limitation.type
              }`,
              is_title: false
            })
          );

    return (
      <div className={containerClassName}>
        {features?.length > 0 &&
          features.map((feature, index) => (
            <div
              key={index}
              className={`flex items-start gap-2 text-sm ${
                feature?.is_title ? 'sm:col-span-full' : ''
              }`}
            >
              {/* Not for title */}
              {!feature?.is_title && (
                <div
                  className="min-w-[20px] mt-[1px] w-5 h-5 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: accentColor
                  }}
                >
                  <svg
                    width="10"
                    height="8"
                    viewBox="0 0 12 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 5L4 8L11 1"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}

              <div
                className={feature?.is_title ? 'font-bold mt-2' : 'opacity-60'}
              >
                {feature?.name}
              </div>
            </div>
          ))}
      </div>
    );
  }

  function checkSubscriptionActiveStatus(orgSubscription: PricingSubscription) {
    if (
      (orgSubscription?.status !== 'active' &&
        orgSubscription?.status !== 'past_due') ||
      (orgSubscription?.status === 'active' &&
        orgSubscription?.cancel_at_period_end &&
        orgSubscription?.cancel_at &&
        new Date() > new Date(parseInt(orgSubscription?.cancel_at + '000'))) ||
      (orgSubscription?.status === 'past_due' &&
        orgSubscription?.current_period_end &&
        new Date() >
          new Date(parseInt(orgSubscription?.current_period_end + '000')))
    ) {
      return false;
    }

    return true;
  }

  function getPricingPlanCTA(planTypeIndex: number, planType: PricingPlanType) {
    const pricingPlan = getPricingPlan(planType);

    if (!pricingPlan || !pricingPlan?.pricing_price.product_id) {
      return <></>;
    }
    // console.log(selectedQuantity, currentSubscriptionQty);

    // Current plan
    if (
      isSubscriptionActive &&
      currentSubscriptionProductId === pricingPlan?.pricing_price.product_id &&
      (pricingPlan?.name?.startsWith('Starter') ||
        selectedQuantity === currentSubscriptionQty)
    ) {
      return (
        <>
          <CLSecondaryCustomButton className="w-full py-2 text-sm cursor-default hover:!bg-transparent">
            Current Plan
          </CLSecondaryCustomButton>
        </>
      );
    }

    return (
      <>
        {/* {planType?.is_freeplan &&
          totalOrgMembers <= CommonConstants.FREE_PLAN_MEMBERS_COUNT && (
            <Link
              className="w-full"
              href={
                '/subscription?plan=' +
                pricingPlan?.id +
                '&qty=' +
                CommonConstants.FREE_PLAN_MEMBERS_COUNT
              }
            >
              <CLSecondaryCustomButton
                className="w-full py-2 text-sm"
                disabled={
                  isLoading ||
                  totalOrgMembers > CommonConstants.FREE_PLAN_MEMBERS_COUNT
                }
              >
                {isLoading &&
                  pricingPlan?.pricing_price.product_id ===
                    selectedProductId && (
                    <LoadingSpinBlackCustom className="w-4 h-4"></LoadingSpinBlackCustom>
                  )}
                Downgrade now
              </CLSecondaryCustomButton>
            </Link>
          )}
        {planType?.is_freeplan &&
          totalOrgMembers > CommonConstants.FREE_PLAN_MEMBERS_COUNT && (
            <>
              <CLSecondaryCustomButton
                className="w-full py-2 text-sm"
                disabled={
                  totalOrgMembers > CommonConstants.FREE_PLAN_MEMBERS_COUNT
                }
              >
                {isLoading &&
                  pricingPlan?.pricing_price.product_id ===
                    selectedProductId && (
                    <LoadingSpinBlackCustom className="w-4 h-4"></LoadingSpinBlackCustom>
                  )}
                Downgrade now
              </CLSecondaryCustomButton>
            </>
          )} */}

        {!planType?.is_freeplan && (
          <Link
            className="w-full"
            href={'/subscription?plan=' + pricingPlan?.id + '&qty=' + 1}
          >
            <CLPrimaryCustomButton
              className="w-full py-2 text-sm"
              disabled={isLoading}
              onClick={() => {
                // changeSubscription(
                //   pricingPlan?.id,
                //   pricingPlan?.pricing_price.product_id as string,
                //   selectedQuantity
                // );
              }}
            >
              {isLoading &&
                pricingPlan?.pricing_price.product_id === selectedProductId && (
                  <LoadingSpinBlackCustom className="w-4 h-4"></LoadingSpinBlackCustom>
                )}
              Upgrade now
            </CLPrimaryCustomButton>
          </Link>
        )}
      </>
    );
  }

  function getLifetimeOfferSection() {
    if (!lifetimePlanType) {
      return <></>;
    }

    // Purely a claim offer, so it exists only while it can actually be claimed:
    // within 24 hours of the organization being created, and not already owned.
    // An organization already on lifetime sees it in the Current Plan card
    // above instead - repeating it here would be an advert for what they own.
    if (!lifetimeOffer.isEligible) {
      return <></>;
    }

    const pricingPlan = getPricingPlan(lifetimePlanType);

    // Nothing to sell without a Stripe id on the plan.
    if (!pricingPlan?.pricing_price?.product_id) {
      return <></>;
    }

    return (
      <section
        className="mt-6 rounded-xl border-[1px] overflow-hidden"
        aria-labelledby="lifetime-offer-heading"
      >
        {/* Accent bar - same visual language as the plan cards below */}
        <div
          className="w-full h-2"
          style={{ backgroundColor: LIFETIME_ACCENT_COLOR }}
        ></div>

        <div className="p-5 sm:p-8 flex flex-col lg:flex-row gap-6 lg:gap-16">
          {/* The offer */}
          <div className="flex flex-col gap-3 sm:gap-4 lg:w-[340px] lg:shrink-0">
            <div className="self-start rounded-full bg-black px-4 py-1.5 text-xs sm:text-sm font-bold text-[#F5C63E]">
              Lifetime deal
            </div>

            <h2
              id="lifetime-offer-heading"
              className="text-xl sm:text-2xl font-bold"
            >
              {lifetimePlanType?.name}
            </h2>

            <div className="flex gap-2 sm:gap-3 items-baseline flex-wrap">
              <div className="text-3xl sm:text-4xl font-extrabold">
                ${pricingPlan.pricing_price.amount}
              </div>
              <div className="text-sm sm:text-base opacity-60">
                one-time payment
              </div>
            </div>

            <div className="text-sm sm:text-base opacity-60">
              Pay once and keep it — no renewals, no monthly bill.
            </div>

            {/* Time left to claim. The section only renders while the offer is
                live, so there is always a deadline to show here. */}
            <div
              className="mt-1 sm:mt-2 self-start rounded-lg px-3 py-2 sm:px-4 sm:py-2.5 text-sm sm:text-base"
              style={{ backgroundColor: LIFETIME_CREAM_COLOR }}
            >
              <span className="opacity-70">Offer ends in </span>
              <LifetimeCountdownComponent
                countdown={lifetimeOffer.countdown}
                size="sm"
              />
              <span className="sr-only">
                {lifetimeOffer.countdown.hours} hours and{' '}
                {lifetimeOffer.countdown.minutes} minutes left
              </span>
            </div>
          </div>

          {/* What it includes, with the CTA anchored beneath it */}
          <div className="flex-grow flex flex-col justify-between gap-5 sm:gap-6">
            <div>
              <div className="font-bold mb-3 sm:mb-4">What&apos;s included</div>
              {getPricingTypeFeatures(
                LIFETIME_CREAM_COLOR,
                lifetimePlanType,
                'grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 sm:gap-y-3'
              )}
            </div>

            <CLButton
              className="flex w-full items-center justify-center gap-2 rounded-full py-3 font-bold text-black transition hover:brightness-105 disabled:opacity-60"
              style={{ backgroundColor: LIFETIME_ACCENT_COLOR }}
              disabled={
                isLifetimeCheckoutLoading || activationState === 'pending'
              }
              onClick={() => {
                startLifetimeCheckout(pricingPlan.id);
              }}
            >
              {isLifetimeCheckoutLoading && (
                <LoadingSpinBlackCustom className="w-4 h-4"></LoadingSpinBlackCustom>
              )}
              Claim lifetime deal
            </CLButton>
          </div>
        </div>
      </section>
    );
  }

  async function startLifetimeCheckout(planId: string) {
    if (!selectedOrganization?.id) {
      return;
    }

    setIsLifetimeCheckoutLoading(true);

    try {
      // A full-page navigation, not a new tab, so Stripe's redirect lands back
      // in this same session. Loading stays on until the browser leaves.
      window.location.href = await createLifetimeCheckoutUrl(
        selectedOrganization.id,
        planId
      );
    } catch (error) {
      setIsLifetimeCheckoutLoading(false);
      setInfoMessage(getLifetimeCheckoutErrorMessage(error));
      setIsAlertPopupOpen(true);
    }
  }

  function openCustomerPortal() {
    setIsCustomerPortalLoading(true);

    // Axios instance
    const protectedAxiosInstance = getProtectedAxiosInstance();

    const payload = {
      orgId: selectedOrganization?.id
    };
    protectedAxiosInstance
      .post('/stripe/get-customer-portal-link', payload)
      .then(
        result => {
          const data = result?.data?.data as { portalUrl: string };
          window.open(data.portalUrl, '_blank');
          setIsCustomerPortalLoading(false);
        },
        error => {
          setIsCustomerPortalLoading(false);
          throw error;
        }
      );
  }

  function cancelOrReactivateSubscription(
    action: string,
    subscriptionId: string
  ) {
    setIsCancelOrReactivateSubscriptionLoading(true);

    // Axios instance
    const protectedAxiosInstance = getProtectedAxiosInstance();

    const payload = {
      orgId: selectedOrganization?.id,
      action,
      subscriptionId
    };
    protectedAxiosInstance
      .post('/stripe/cancel-or-reactivate-subscription', payload)
      .then(
        result => {
          const data = result?.data?.data as {
            action: string;
            subscriptionData: {
              cancel_at?: number | undefined;
              cancel_at_period_end?: boolean;
              canceled_at?: number | undefined;
            };
          };

          // Update the organization data
          if (organization?.pricing_subscription) {
            // 1) clone the old subscription and apply your updates
            const updatedSub: PricingSubscription = {
              ...organization.pricing_subscription,
              canceled_at: data.subscriptionData.canceled_at,
              cancel_at_period_end: data.subscriptionData.cancel_at_period_end
            };

            // 2) clone the entire organization, replacing only the subscription
            setOrganization({
              ...organization,
              pricing_subscription: updatedSub
            });
          }

          setIsCancelOrReactivateSubscriptionLoading(false);

          // Show churn survey
          if (action == 'cancel') {
            setIsChurnSurveyPopupOpen(true);
          }
        },
        error => {
          setIsCancelOrReactivateSubscriptionLoading(false);
          throw error;
        }
      );
  }

  return (
    <>
      {/* Lifetime checkout outcome */}
      {activationState === 'pending' && (
        <div
          role="status"
          aria-live="polite"
          className="mt-6 max-w-md p-4 rounded-lg border-[1px] flex items-start gap-3"
        >
          <LoadingSpinBlackCustom className="w-4 h-4 mt-1"></LoadingSpinBlackCustom>
          <div>
            <div className="font-medium">Activating your plan…</div>
            <div className="text-sm opacity-60">
              Payment received. This usually takes a couple of seconds.
            </div>
          </div>
        </div>
      )}

      {activationState === 'activated' && (
        <div
          role="status"
          aria-live="polite"
          className="mt-6 max-w-md p-4 rounded-lg border-[1px] bg-green-50 border-green-200"
        >
          <div className="font-medium text-green-700">
            You&apos;re on the lifetime plan.
          </div>
          <div className="text-sm text-green-700 opacity-80">
            Thank you for your support — there is nothing left to renew.
          </div>
        </div>
      )}

      {activationState === 'timed-out' && (
        <div
          role="status"
          aria-live="polite"
          className="mt-6 max-w-md p-4 rounded-lg border-[1px] bg-amber-50 border-amber-200"
        >
          <div className="font-medium text-amber-800">
            Your payment went through.
          </div>
          <div className="text-sm text-amber-800 opacity-80">
            Your plan is still being activated. Please refresh this page in a
            moment — if it still looks wrong, contact{' '}
            <a className="underline" href="mailto:hello@sacca.ai">
              hello@sacca.ai
            </a>
            .
          </div>
        </div>
      )}

      {checkoutStatus === 'cancelled' && (
        <div
          role="status"
          aria-live="polite"
          className="mt-6 max-w-md p-4 rounded-lg border-[1px]"
        >
          <div className="font-medium">Checkout cancelled</div>
          <div className="text-sm opacity-60">
            Nothing was charged and your plan is unchanged.
          </div>
        </div>
      )}

      {/* Current plan details */}
      {organization && (
        <div className="mt-6 max-w-md p-4 rounded-lg border-[1px]">
          {/* Status */}
          <div className="flex justify-between items-center">
            <div className=" py-1">Current Plan</div>
            {isSubscriptionActive ? (
              <div className="rounded-full bg-green-50 text-green-600 text-xs px-2 py-1">
                Active
              </div>
            ) : (
              <div className="rounded-full bg-red-50 text-red-600 text-xs px-2 py-1">
                Not active
              </div>
            )}
          </div>

          {/* Plan details */}
          <div className="flex justify-between mt-4">
            <div className="flex flex-col gap-2">
              <div className="text-2xl font-bold">
                {
                  organization?.pricing_subscription?.pricing_plan
                    ?.pricing_plan_type?.name
                }
              </div>
              {!organization?.pricing_subscription?.pricing_plan
                ?.pricing_plan_type?.is_freeplan &&
                !organization?.pricing_subscription?.pricing_plan
                  ?.pricing_plan_type?.is_lifetime &&
                !organization?.pricing_subscription?.pricing_plan
                  ?.pricing_plan_type?.is_enterprise && (
                  <div className="text-sm">
                    Billed{' '}
                    {
                      organization?.pricing_subscription?.pricing_plan
                        ?.pricing_period?.name
                    }
                  </div>
                )}
            </div>
            <div className="flex flex-col gap-2">
              {organization?.pricing_subscription?.qty && (
                <div className="text-2xl font-bold">
                  {organization?.pricing_subscription?.pricing_plan?.name.startsWith(
                    'Starter'
                  )
                    ? 3
                    : organization?.pricing_subscription?.pricing_plan?.name.startsWith(
                        'Large'
                      )
                    ? largeQuantity
                    : organization?.pricing_subscription?.pricing_plan?.name.startsWith(
                        'Small'
                      )
                    ? smallQuantity
                    : organization?.pricing_subscription?.qty || 0}{' '}
                  members
                </div>
              )}
            </div>
          </div>

          {/* Apply coupon */}
          <div className="mt-4 flex justify-between flex-wrap gap-2">
            {/* <div>
              <CouponApplyComponent></CouponApplyComponent>
            </div> */}
            <div>
              {/* Cancel or Reactive Subscription */}
              {!organization?.pricing_subscription?.pricing_plan
                ?.pricing_plan_type?.is_freeplan &&
                !organization?.pricing_subscription?.pricing_plan
                  ?.pricing_plan_type?.is_lifetime &&
                organization?.pricing_subscription?.subscription_id &&
                organization?.pricing_subscription?.status == 'active' && (
                  <div className="flex items-center gap-2">
                    {/* Cancel subscription */}
                    {!organization?.pricing_subscription
                      ?.cancel_at_period_end && (
                      <CLSecondaryCustomButton
                        className="px-2"
                        disabled={isCancelOrReactivateSubscriptionLoading}
                        onClick={() => {
                          cancelOrReactivateSubscription(
                            'cancel',
                            organization?.pricing_subscription
                              ?.subscription_id as string
                          );
                        }}
                      >
                        Cancel subscription
                      </CLSecondaryCustomButton>
                    )}

                    {/* Reactivate subscription */}
                    {organization?.pricing_subscription
                      ?.cancel_at_period_end && (
                      <CLSecondaryCustomButton
                        className="px-2"
                        disabled={isCancelOrReactivateSubscriptionLoading}
                        onClick={() => {
                          cancelOrReactivateSubscription(
                            'reactivate',
                            organization?.pricing_subscription
                              ?.subscription_id as string
                          );
                        }}
                      >
                        Reactivate subscription
                      </CLSecondaryCustomButton>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Lifetime offer - shown only when the pricing tables carry one */}
      {getLifetimeOfferSection()}

      {/* List of plans */}
      <div className="mt-4 max-w-full">
        {/* Why Checklist */}
        <div className="p-2 opacity-60">
          Why should I pay for Checklist? We don&apos;t run ads. We don&apos;t
          sell your data. We are 100% supported by our members.
        </div>

        {/* Title */}
        <div className="flex justify-between flex-wrap p-2">
          {/* Only worth distinguishing while the lifetime section is actually
              on screen; otherwise these really are all the plans on offer. */}
          <div>
            {lifetimeOffer.isEligible ? 'Subscription plans' : 'All plans'}
          </div>

          {/* Period */}
          <div className="flex items-center gap-3">
            {pricingVersion &&
              pricingVersion?.pricing_periods?.length > 0 &&
              pricingVersion.pricing_periods.map((version, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={version?.id}
                    value={version?.id}
                    checked={selectedPeriodId === version.id}
                    onChange={event => {
                      setSelectedPeriodId(event.currentTarget.value);
                    }}
                    name="pricing_period"
                    className="bg-white border-black cursor-pointer text-black focus:ring-black/20 rounded-full w-4 h-4"
                  />
                  <label className="cursor-pointer" htmlFor={version?.id}>
                    {version?.description}
                  </label>
                </div>
              ))}
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-2">
          {subscriptionPlanTypes &&
            subscriptionPlanTypes.map((planType, planTypeIndex) => (
              <div
                key={planTypeIndex}
                className="rounded-lg border-[1px] overflow-hidden flex flex-col"
              >
                {/* Color */}
                <div
                  className={`w-full h-2`}
                  style={{ backgroundColor: planColors[planTypeIndex] }}
                ></div>

                {/* Details */}
                <div className="flex-grow px-4 py-8 flex flex-col gap-8">
                  {/* Title */}
                  <div className="text-xl">{planType?.name}</div>

                  {/* Price */}
                  <div className="sm:min-h-[40px]">
                    <PricingPlanPrice
                      planType={planType}
                      totalOrgMembers={totalOrgMembers}
                      selectedQuantity={selectedQuantity}
                      setSelectedQuantity={setSelectedQuantity}
                      setInfoMessage={setInfoMessage}
                      setIsAlertPopupOpen={setIsAlertPopupOpen}
                    />
                  </div>

                  {/* CTA */}
                  <div className="sm:h-[38px] flex justify-center items-center">
                    {/* All plans except Enterprise plan */}
                    {!planType?.is_enterprise &&
                      getPricingPlanCTA(planTypeIndex, planType)}

                    {/* Enterprise plan */}
                    {planType?.is_enterprise && (
                      <a
                        href="mailto:hello@sacca.ai?subject=Enterprise%20Plan%20Inquiry%20-%20Custom%20Solution&body=Hello%20Checklist%20Sales%20Team,%0D%0A%0D%0AI%20am%20interested%20in%20the%20Enterprise%20Plan%20for%20checklist.gg.%20I%20am%20looking%20for%20a%20solution%20that%20supports%20high-volume%20usage,%20white-label%20customization,%20SSO%20with%20SAML,%20and%20priority%20support.%20Could%20you%20please%20provide%20more%20details%20on%20pricing%20and%20how%20we%20can%20tailor%20this%20plan%20to%20my%20organization's%20needs?%0D%0A%0D%0AThank%20you,%0D%0A[Your%20Name]"
                        className="w-full"
                      >
                        <CLSecondaryCustomButton className="w-full py-2 text-sm">
                          Contact sales
                        </CLSecondaryCustomButton>
                      </a>
                    )}
                  </div>

                  {/* Features */}
                  <div className="flex-grow">
                    {getPricingTypeFeatures(
                      planColors[planTypeIndex],
                      planType
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Customer portal link */}
      <div className="mt-6 text-sm opacity-60">
        To download invoices, to update card and billing information - visit{' '}
        <CLButton
          className="underline"
          disabled={isCustomerPortalLoading}
          onClick={() => {
            openCustomerPortal();
          }}
        >
          Stripe Customer Portal ↗
        </CLButton>
      </div>

      {/* Payment status */}
      {router?.query?.['payment_intent'] &&
        router?.query?.['payment_intent_client_secret'] && (
          <PaymentStatusPopupComponent
            payment_intent={router?.query?.['payment_intent'] as string}
            payment_intent_client_secret={
              router?.query?.['payment_intent_client_secret'] as string
            }
          />
        )}

      {/* Alert message */}
      <AlertPopupComponent
        title="Information"
        isOpen={isAlertPopupOpen}
        setOpen={setIsAlertPopupOpen}
      >
        {infoMessage}
      </AlertPopupComponent>

      {/* Post subscription survey popup */}
      <PostSubscriptionPopupComponent
        isOpen={isPostSubscriptionPopupOpen}
        setOpen={setIsPostSubscriptionPopupOpen}
        orgId={selectedOrganization?.id}
        email={userEmail}
      />

      {/* Churn survey popup */}
      <ChurnSurveyPopupComponent
        isOpen={isChurnSurveyPopupOpen}
        setOpen={setIsChurnSurveyPopupOpen}
        orgId={selectedOrganization?.id}
        email={userEmail}
      />
    </>
  );
}
